import { useEffect, useRef } from "react";

import { logCodeCellError } from "@moment/state";

import { isRejectedResult } from "~/components/canvas/easel/runtime/interfaces";
import type { CachedCell, CellInfo } from "~/models/cell";

/**
 * Logs code cell errors (compile-time and runtime) to the document's
 * `.moment/runtime-errors.log` file so that agents can read them.
 */
export function useLogCellErrors(
	cellInfo: CellInfo | undefined,
	cachedCell: CachedCell | undefined,
	documentId: string | undefined,
	pageId: string | undefined
) {
	const lastLoggedError = useRef<string | null>(null);

	useEffect(() => {
		if (!documentId || !cellInfo) return;

		const cellId = cellInfo.cell.id;

		if (cellInfo.status === "error") {
			const key = `compile:${cellId}:${cellInfo.message}`;
			if (lastLoggedError.current === key) return;
			lastLoggedError.current = key;
			logCodeCellError({
				documentId,
				pageId,
				cellId,
				error: new Error(cellInfo.message),
			});
			return;
		}

		const result = cachedCell?.result;
		if (result?.staticError) {
			const key = `static:${cellId}:${result.staticError.message}`;
			if (lastLoggedError.current === key) return;
			lastLoggedError.current = key;
			logCodeCellError({
				documentId,
				pageId,
				cellId,
				error: result.staticError,
			});
			return;
		}

		if (isRejectedResult(result)) {
			const key = `runtime:${cellId}:${result.runtimeError.message}`;
			if (lastLoggedError.current === key) return;
			lastLoggedError.current = key;
			logCodeCellError({
				documentId,
				pageId,
				cellId,
				error: result.runtimeError,
			});
			return;
		}

		lastLoggedError.current = null;
	}, [cellInfo, cachedCell?.result, documentId, pageId]);
}
